import api from '@/services/api';
import type {
    AvailablePermission,
    AvailableRole,
    PermissionProvenance,
    UserRole,
} from '../types/users.types';
import { compareRolesByLabel } from '../utils/roleLabel';

const rbacUsersPath = (id: string) => `/rbac/users/${id}`;

// Rbac-service responses do not use a `{ success, data }` envelope: list endpoints
// return bare arrays and resource endpoints return the resource object directly.
// HTTP status is the success signal (Axios throws on non-2xx).

export const fetchUserRoles = async (id: string): Promise<UserRole[]> => {
    const response = await api.get<UserRole[]>(`${rbacUsersPath(id)}/roles`);
    return response.data;
};

export const fetchUserPermissions = async (id: string): Promise<PermissionProvenance> => {
    const response = await api.get<PermissionProvenance>(`${rbacUsersPath(id)}/permissions`);
    return response.data;
};

export const assignUserRole = async (userId: string, roleId: string): Promise<void> => {
    await api.post<{ message: string }>(`${rbacUsersPath(userId)}/roles`, {
        role_id: roleId,
    });
};

export const removeUserRole = async (userId: string, roleId: string): Promise<void> => {
    await api.delete<{ message: string }>(`${rbacUsersPath(userId)}/roles/${roleId}`);
};

export const grantUserPermission = async (userId: string, permissionId: string): Promise<void> => {
    await api.post<{ message: string }>(`${rbacUsersPath(userId)}/permissions`, {
        permission_id: permissionId,
    });
};

export const revokeUserPermission = async (userId: string, permissionId: string): Promise<void> => {
    await api.delete<{ message: string }>(`${rbacUsersPath(userId)}/permissions/${permissionId}`);
};

export const fetchAvailableRoles = async (): Promise<AvailableRole[]> => {
    const response = await api.get<AvailableRole[]>('/rbac/roles');

    // Rbac-service orders by `name`; the UI shows `display_name`, so re-sort on the label.
    return [...response.data].sort(compareRolesByLabel);
};

export const fetchAvailablePermissions = async (): Promise<AvailablePermission[]> => {
    const response = await api.get<AvailablePermission[]>('/rbac/permissions');
    return response.data;
};
